/**
 * Centralized Numeric Service for Frontend
 * 
 * Central service for ALL numeric parsing and formatting in the frontend.
 * This enforces consistency and prevents floating point precision issues.
 * 
 * IMPORTANT: Use this service instead of native parseInt/parseFloat
 */

import { formatBasisPointsShort, toPercentage } from '@zlinkx/shared-v2';

const moneyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US');

/**
 * Central numeric parsing and formatting service
 */
export const Numeric = {
  /**
   * Parse integer value from unknown input
   * @param value - Value to parse
   * @param defaultValue - Default value if parsing fails
   * @returns Parsed integer or default value
   */
  parseInt(value: unknown, defaultValue: number = 0): number {
    if (typeof value === 'number') {
      return Math.trunc(value);
    }
    
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    
    return defaultValue;
  },

  /**
   * Parse monetary value in cents from unknown input
   * @param value - Value to parse (can be dollars or cents)
   * @param defaultValue - Default value if parsing fails
   * @returns Amount in cents
   */
  parseMoney(value: unknown, defaultValue: number = 0): number {
    if (typeof value === 'number') {
      // If it's already an integer, assume it's in cents
      if (Number.isInteger(value)) {
        return value;
      }
      // If it has decimals, assume it's in dollars and convert to cents
      return Math.round(value * 100);
    }
    
    if (typeof value === 'string') {
      // Remove currency symbols and whitespace
      const cleaned = value.replace(/[$,\s]/g, '');
      const parsed = Number.parseFloat(cleaned);
      if (isNaN(parsed)) {
        return defaultValue;
      }
      // Convert to cents
      return Math.round(parsed * 100);
    }
    
    return defaultValue;
  },

  /**
   * Parse basis points from unknown input
   * @param value - Value to parse (can be percentage or basis points)
   * @param isPercentage - If true, input is percentage (e.g., 2.5 for 2.5%)
   * @param defaultValue - Default value if parsing fails
   * @returns Basis points (e.g., 250 for 2.5%)
   */
  parseBasisPoints(value: unknown, isPercentage: boolean = false, defaultValue: number = 0): number {
    let numericValue: number;
    
    if (typeof value === 'number') {
      numericValue = value;
    } else if (typeof value === 'string') {
      // Remove % symbol if present
      const cleaned = value.replace(/%/g, '').trim();
      numericValue = Number.parseFloat(cleaned);
      if (isNaN(numericValue)) {
        return defaultValue;
      }
    } else {
      return defaultValue;
    }
    
    // Convert from percentage to basis points if needed
    const result = isPercentage ? Math.round(numericValue * 100) : Math.round(numericValue);
    
    // Validate range (0-10000 basis points = 0-100%)
    if (result < 0 || result > 10000) {
      return defaultValue;
    }
    
    return result;
  },

  /**
   * Format money from cents to display string
   * @param cents - Amount in cents
   * @param includeSign - Include dollar sign (default: true)
   * @returns Formatted string (e.g., "$12.34")
   */
  formatMoney(cents: number, includeSign: boolean = true): string {
    const dollars = cents / 100;
    const formatted = dollars.toFixed(2);
    return includeSign ? `$${formatted}` : formatted;
  },

  /**
   * Format money with thousands separator
   * @param cents - Amount in cents
   * @returns Formatted string (e.g., "$1,234.56")
   */
  formatMoneyWithCommas(cents: number): string {
    const dollars = cents / 100;
    return `$${moneyFormatter.format(dollars)}`;
  },

  /**
   * Format basis points to percentage string
   * @param value - Basis points value
   * @param precision - Number of decimal places (default: 2)
   * @returns Formatted string (e.g., "2.50%")
   */
  formatBasisPoints(value: number, precision: number = 2): string {
    const digits = Number.isInteger(precision) ? Math.min(Math.max(precision, 0), 6) : 2;
    const pct = toPercentage(value);
    return `${pct.toFixed(digits)}%`;
  },

  /**
   * Format basis points to percentage string (short format)
   * @param value - Basis points value
   * @returns Formatted string without trailing zeros (e.g., "2.5%")
   */
  formatBasisPointsShort(value: number): string {
    return formatBasisPointsShort(value);
  },

  /**
   * Format number with commas
   * @param value - Number to format
   * @returns Formatted string (e.g., "1,234")
   */
  formatWithCommas(value: number): string {
    return integerFormatter.format(value);
  },

  /**
   * Validate if a value is a valid integer
   * @param value - Value to check
   * @returns true if value is a valid integer
   */
  isValidInteger(value: unknown): boolean {
    if (typeof value === 'number') {
      return Number.isInteger(value);
    }
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10);
      return !isNaN(parsed) && parsed.toString() === value.trim();
    }
    return false;
  },

  /**
   * Validate if a value is within a range
   * @param value - Value to check
   * @param min - Minimum value (inclusive)
   * @param max - Maximum value (inclusive)
   * @returns true if value is within range
   */
  isInRange(value: number, min: number, max: number): boolean {
    return value >= min && value <= max;
  },
};
